import { modelState, ModelStateList } from "@/utils/modelState";
import { addDays } from 'date-fns';
import { PaymentTypes } from "./PaymentTypes/PaymentType";
import { RefundTypes } from "./RefundTypes/RefundType";
import { ReservationArrivalTransferMode } from "./ReservationArrivalTransferMode";
import { ReservationDepartureTransferMode } from "./ReservationDepartureTransferMode";
import { ReservationLogEntry } from "./ReservationLogEntry";
import { ReservationQuoteStatuses } from "./ReservationQuoteStatuses/ReservationQuoteStatus";
import { createReservationQuoteStatus, createReservationReleasedStatus, createReservationReservedStatus, isReservationQuoteStatus, isReservationReleasedStatus, isReservationReservedStatus } from "./ReservationStatus";
import { TaxDocumentTypes } from "./TaxDocumentTypes/TaxDocumentType";
import { fetchModel } from './model';
import { buildSaveReservationRequest } from "./model.Reservations";
import { ReservationState } from './model.roomsAndTotals';
import { trackSaveProgress } from "./saveProgress";
import {
    VersionHistoryResponse
} from "./types";
export async function saveReservation(reservation: ReservationState, newStatus: "Reserved" | "Released" | "Quote" | null): Promise<boolean> {
    const sourceError = reservation.InquirySource?.validation();
    if (sourceError) {
        reservation.SaveProgress.setValue({ phase: "failed", label: "Save failed", error: sourceError });
        return false;
    }
    reservation.IsLoading.setValue(true);
    reservation.SaveProgress.setValue({ phase: "saving", label: "Saving changes…" });
    if (newStatus === "Reserved" && !isReservationReservedStatus(reservation.ReservationStatus.value)) {
        reservation.ReservationStatus.setValue(createReservationReservedStatus());
    }
    else if (newStatus === "Released" && !isReservationReleasedStatus(reservation.ReservationStatus.value)) {
        const released = createReservationReleasedStatus();
        reservation.ReservationStatus.setValue(released);
    }
    else if (newStatus === "Quote" && !isReservationQuoteStatus(reservation.ReservationStatus.value)) {
        reservation.ReservationStatus.setValue(createReservationQuoteStatus());
        if (!reservation.Quote.value) {
            reservation.Quote.setValue({
                ExpiresAt: modelState(addDays(new Date(), 3)),
                Status: modelState(ReservationQuoteStatuses.createReservationQuoteInitialEnquiryStatus()),
                StatusIsDirty: modelState(false),
                StatusVersion: modelState(0),
                TempReservationEnabled: modelState(false),
                TempReservationReleaseAfter: modelState(null)
            });
        }
    }
    const request = buildSaveReservationRequest(reservation);
    const allowPastDates = reservation.AllowPastDates.value ? 'true' : 'false';
    try {
        const response = await fetch(`/save-reservation?allowPastDates=${allowPastDates}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(request)
        });
        if (!response.ok) {
            if (response.status === 400) {
                let problem: any = null;
                try {
                    problem = await response.json();
                }
                catch { /* not JSON - fall through to generic error */ }
                // ProblemDetails.Extensions is [JsonExtensionData], so entries flatten to the JSON root - code lands at problem.code, not problem.extensions.code
                const code = problem?.code;
                if (code === 'past-date-blocked') {
                    throw new Error("This reservation includes a date before today. Turn on 'Allow past dates (backfill)' to save it as a backfill / historical entry.");
                }
            }
            throw new Error(`Save failed (${response.status})`);
        }
        const saveResponse = await response.json() as SaveReservationResponse;
        // POST is done (price calc + enqueue). The background flow is now applying the booking changes.
        reservation.SaveProgress.setValue({ phase: "processing", label: "Processing changes…" });
        // Track the async flow with live progress (SignalR) and a poll fallback. Surfaces which step
        // the save is on and resolves 'failed' with a reason instead of spinning forever.
        const outcome = await trackSaveProgress(saveResponse.reservationId, saveResponse.changeRequestId, p => reservation.SaveProgress.setValue(p));
        if (outcome === 'failed') {
            // Leave the failure visible on the spinner; stop loading so the user can retry/close.
            reservation.IsLoading.setValue(false);
            return false;
        }
        // Keep the overlay on the completed checklist (all steps ✓) while we refresh - clearing it here
        // would re-render the checklist from the start ("Saving changes") until IsLoading flips.
        reservation.SaveProgress.setValue({ phase: "complete", label: "Saved" });
        const type = isReservationQuoteStatus(reservation.ReservationStatus.value) ? 'quote' : 'reserved';
        await (window as any).salesSchedule.refresh(type, reservation);
        //closeSchedulePopupDialog();
        reservation.IsLoading.setValue(false);
        reservation.SaveProgress.setValue(null);
        return true;
    }
    catch (e) {
        // Never leave the spinner stuck - show the error and stop. Returning false keeps the editor
        // dialog open (the caller only closes on a truthy result) so the failure overlay stays visible
        // and the user can act on it - e.g. tick "Allow past dates (backfill)" and retry.
        reservation.SaveProgress.setValue({ phase: "failed", label: "Save failed", error: e instanceof Error ? e.message : String(e) });
        reservation.IsLoading.setValue(false);
        return false;
    }
}

export interface SaveReservationResponse {
    reservationId: string;
    changeRequestId: string;
}

export async function pollForChangeRequest(reservationId: string, changeRequestId: string) {
    // Poll for change request response is no longer in progress every 1 second
    const getChangeRequestInProgress = async () => {
        const response = await fetch(`/reservations/${reservationId}/change-requests/${changeRequestId}/in-progress`);
        const inProgress = await response.text();
        return inProgress === 'true';
    };
    while (await getChangeRequestInProgress()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
}

export async function fetchVersionHistory(reservationId: string): Promise<VersionHistoryResponse> {
    const response = await fetch(`/reservations/${reservationId}/version-history`);
    return await response.json();
}

export async function revertToVersion(reservationId: string, version: number): Promise<SaveReservationResponse> {
    const response = await fetch(`/reservations/${reservationId}/revert-to-version`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify({ Version: version })
    });
    return await response.json();
}

export async function fetchHistoricalVersion(reservationId: string, version: number): Promise<ReservationState> {
    return await fetchModel(`/reservations/${reservationId}/versions/${version}`);
}

export interface ArrivalTransferDetailsState {
    Mode: modelState<ReservationArrivalTransferMode | null>;
}

export interface DepartureTransferDetailsState {
    Mode: modelState<ReservationDepartureTransferMode | null>;
}

export interface Voucher {
    VoucherId: string;
    Name: string;
    Code: string;
    Active: boolean;
}

export interface ReservationPayment {
    CreatedDate: Date;
    PaymentId: string;
    PaymentType: PaymentTypes.PaymentType;
    Amount: number;
    Transactions: ReservationPaymentTransaction[];
}

export interface ReservationPaymentTransaction {
    CreatedDate: Date;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentType;
    IsVoid?: boolean;
}

export interface ReservationRefund {
    CreatedDate: Date;
    RefundId: string;
    RefundType: RefundTypes.RefundType;
    Amount: number;
    Transactions: ReservationRefundTransaction[];
}

export interface ReservationRefundTransaction {
    CreatedDate: Date;
    EffectiveDate: Date;
    Amount: number;
    Note: string;
    TaxDocument: TaxDocumentTypes.TaxDocumentType;
    IsVoid?: boolean;
}

export interface ReservationNote {
    NoteId: string;
    NoteText: string;
    CreatedBy: string;
    CreatedAt: Date;
}

export interface ReservationLogEntryContainer {
    LogEntryId: string;
    LogText: string;
    LogData: ReservationLogEntry;
    CreatedBy: string;
    CreatedAt: Date;
}

export interface NewPersonDto {
    PersonId: string;
    FirstName: modelState<string>;
    LastName: modelState<string | null>;
    PhoneNumber: modelState<string | null>;
    Email: modelState<string | null>;
    PersonDto: PersonDto & {
        [key: string]: Object;
    };
}

export interface PersonDto {
    PersonId: string;
    ContactId: string | null;
    FirstName: string;
    LastName: string | null;
    PhoneNumber: string | null;
    Email: string | null;
    Credits: PersonCredit[];
}

export interface PersonCredit {
    CreditId: string;
    Amount: number;
    ExpiresOn: Date;
    SequenceNumber: number;
}

export interface PersonCreditDto {
    CreditId: string;
    Amount: number;
    ExpiresOn: string;
    SequenceNumber: number;
}

export interface ReservationProfileCacheDto {
    PersonId: string;
    Credits: PersonCreditDto[];
}

export interface ServerPersonWithCreditsDto {
    PersonId: string;
    ContactId: string | null;
    FirstName: string;
    LastName: string | null;
    PhoneNumber: string | null;
    Email: string | null;
    Credits: ServerPersonCreditDto[];
}

export interface ServerPersonCreditDto {
    CreditId: string;
    Amount: number;
    ExpiresOn: string;
    SequenceNumber: number;
}

export interface RoomBlockDateDto {
    Date: string;
    Rooms: RoomBlockDateRoomDto[];
}

export interface RoomBlockDateRoomDto {
    LocationId: string;
    IsAllDay: boolean;
    Start: string;
    End: string;
    ReservationRoomId: string | null;
}

export interface RoomBlockDatesContainer {
    Dates: ModelStateList<RoomBlockDate>;
    Version: modelState<number>;
}

export interface RoomBlockDate {
    Date: Date;
    Rooms: ModelStateList<RoomBlockDateRoom>;
    Version: modelState<number>;
}

export interface RoomBlockDateRoom {
    LocationId: string;
    IsAllDay: boolean;
    Start: Date;
    End: Date;
    ReservationRoomId: string | null;
}
